import React, { useCallback, useMemo, useState } from "react";
import { SecondaryButtons } from "../common/buttons";
import { RadioGroup, RadioGroupItem, Label, Badge, Input } from "../shadcn";
import { AsyncValidateOnTypeInput } from "../core/AsyncValidateOnTypeInput";
import FileInputButton from "../core/FileInputButton";
import FileDragAndDropTarget from "./FileDragAndDropTarget";
import { LOOKUP_TYPES, CONTACT_MORE_CREDITS, MAX_UPLOAD_RECORDS } from "./constants";
import ComboBox from "../core/ComboBox";
import { useGetCustomUploadsSourcesQuery } from "src/hooks/useCustomUploads";
import { AddressSearchBox } from "src/components/neighborhoodSearch/components/AddressSearchBox";
import InfoHint from "src/components/common/InfoHint";

const NUM_PREVIEW_ROWS = 20;

const StepContainer = ({ children }) => (
  <div className="flex flex-col gap-4 border border-gray-300 bg-white p-6 rounded-xl shadow-sm">{children}</div>
);

const Step = ({ stepNumber, stepText, children }) => {
  return (
    <div className="flex flex-col gap-3 items-center text-center">
      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
        {stepNumber}
      </div>
      <span className="font-semibold text-lg text-gray-primary">{stepText}</span>
      <div className="font-normal text-sm text-gray-secondary">{children}</div>
    </div>
  );
};

const AddSource = ({ listSource, onListSourceChange, stepNumber }) => {
  const { data: sources } = useGetCustomUploadsSourcesQuery();
  const [newItem, setNewItem] = useState(null);
  const [sourceListSearchTerm, setSourceListSearchTerm] = useState("");

  const handleListSourceChange = (newSelected, isNew) => {
    isNew && setNewItem(newSelected);
    onListSourceChange(newSelected);
  };

  const sourcesList = useMemo(() => {
    return [
      ...(sources || []).map(({ source }) => ({
        label: source,
        value: source,
      })),
      ...(newItem ? [newItem] : []),
    ];
  }, [sources, newItem]);

  return (
    <StepContainer>
      <Step stepNumber={stepNumber} stepText="Add Source (Optional)">
        Specify a source for this look up to help you remember where this data
        came from.
      </Step>
      <ComboBox
        inputValue={sourceListSearchTerm}
        setInputValue={setSourceListSearchTerm}
        isNewItemsAllowed
        placeholder="Select your field"
        emptyText="No matching fields"
        list={sourcesList}
        value={listSource}
        onSelect={(newSelected, isNew) => {
          handleListSourceChange(
            {
              label: newSelected ? newSelected.value : null,
              value: newSelected ? newSelected.value : null,
            },
            isNew
          );
        }}
      />
    </StepContainer>
  );
};

const AddName = ({
  listName,
  onListNameChange,
  stepNumber,
  importNameValidator,
}) => {
  const handleListNameChange = (name) => {
    onListNameChange(name);
  };
  return (
    <StepContainer>
      <Step stepNumber={stepNumber} stepText="Name Your List">
        Specify a name for this lookup so you can refer back to it in the
        future.
      </Step>
      <AsyncValidateOnTypeInput
        onValidate={importNameValidator}
        value={listName}
        onChange={handleListNameChange}
        placeHolder="Name your list"
      />
    </StepContainer>
  );
};

const PhoneInput = ({ phoneNumber, onPhoneNumberChange }) => (
  <Input
    type="tel"
    placeholder="Enter phone number..."
    value={phoneNumber}
    onChange={(e) => onPhoneNumberChange(e.target.value)}
    className="w-full"
  />
);

const SelectSource = ({
  onFileSelected,
  stepNumber,
  lookupType,
  phoneNumber,
  onPhoneNumberChange,
  onAddressSelected,
  selectedAddress,
}) => (
  <StepContainer>
    <Step stepNumber={stepNumber} stepText="Select Inputs">
      <p>
        {lookupType === LOOKUP_TYPES.reverseAddress
          ? "Upload a file with multiple records or enter a single address."
          : "Upload a file with multiple records or enter a single phone number."}
      </p>
    </Step>

    <div className="flex flex-col gap-4">
      <FileDragAndDropTarget
        onFileSelected={onFileSelected}
        className="flex flex-col gap-2 border-2 border-dashed p-4 rounded-lg"
      >
        <FileInputButton
          onFileSelected={onFileSelected}
          buttonLabel="Select File"
        />
        <p className="text-sm text-gray-secondary text-center">
          Drag and drop or click to upload<br />
          <span className="text-xs">Supports .xls, .csv, and .txt files (max {MAX_UPLOAD_RECORDS.toLocaleString()} records)</span>
        </p>
        <InfoHint>Providing a <span className="font-bold text-gray-primary">Name</span> column will greatly boost accuracy.</InfoHint>
      </FileDragAndDropTarget>

      <div className="flex items-center gap-3">
        <div className="flex-1 border-t border-gray-300" />
        <span className="text-sm font-semibold text-gray-secondary uppercase">or</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>

      {lookupType === LOOKUP_TYPES.reverseAddress ? (
        <AddressSearchBox
          onAddressSelected={onAddressSelected}
          onLocationChange={() => {}}
          placeholder="Search Single Address"
          initialValue={selectedAddress?.formattedAddress}
        />
      ) : (
        <PhoneInput
          phoneNumber={phoneNumber}
          onPhoneNumberChange={onPhoneNumberChange}
        />
      )}
    </div>
  </StepContainer>
);

const SelectLookupType = ({ lookupType, onLookupTypeChange, stepNumber }) => (
  <StepContainer>
    <Step stepNumber={stepNumber} stepText="Select Look Up Type">
      <p className="font-normal text-[14px] text-gray-secondary">
        Pick one of the following.
      </p>
    </Step>
    <RadioGroup
      value={lookupType}
      onValueChange={onLookupTypeChange}
      className="space-y-2"
    >
      <div className="flex items-start space-x-3">
        <RadioGroupItem value={LOOKUP_TYPES.reverseAddress} id="address" />
        <div className="grid gap-1.5 leading-none">
          <Label htmlFor="address" className="text-sm font-medium text-gray-primary">
            Find missing phone numbers
          </Label>
          <p className="text-[12px] text-gray-secondary">
            <span className="font-bold text-gray-primary">Address</span> is required for phone number lookups.
          </p>
        </div>
      </div>
      <div className="flex items-start space-x-3">
        <RadioGroupItem value={LOOKUP_TYPES.reversePhone} id="phone" />
        <div className="grid gap-1.5 leading-none">
          <Label
            htmlFor="phone"
            className="text-sm font-medium text-gray-primary"
          >
            Find missing addresses
          </Label>
          <p className="text-[12px] text-gray-secondary">
            <span className="font-bold text-gray-primary">Phone number</span> is required for address lookups.
          </p>
        </div>
      </div>
    </RadioGroup>
  </StepContainer>
);

const steps = ["selectLookupType", "selectSource", "addName", "addSource"].map(
  (stepName, index) => ({
    stepNumber: index + 1,
    stepName,
  })
);

const ConfigureUpload = ({
  importNameValidator,
  userCredits,
  numRecords,
  jsonData,
  onFileSelected,
  onListNameChange,
  fileName,
  listName,
  fileColumnsKeys,
  onListSourceChange,
  onLookupTypeChange,
  lookupType,
  listSource,
  phoneNumber,
  onPhoneNumberChange,
  onAddressSelected,
  selectedAddress,
}) => {

  return (
    <section className="flex flex-col gap-8 pageMargin h-full">
      <div className={`grid grid-cols-1 md:grid-cols-${steps.length} gap-6`}>
        {steps.map(({ stepName, stepNumber }) => {
          switch (stepName) {
            case "selectLookupType":
              return (
                <SelectLookupType
                  key={"selectLookupType"}
                  lookupType={lookupType}
                  onLookupTypeChange={onLookupTypeChange}
                  stepNumber={stepNumber}
                />
              );
            case "selectSource":
              return (
                <SelectSource
                  key="selectSource"
                  onFileSelected={onFileSelected}
                  stepNumber={stepNumber}
                  lookupType={lookupType}
                  phoneNumber={phoneNumber}
                  onPhoneNumberChange={onPhoneNumberChange}
                  onAddressSelected={onAddressSelected}
                  selectedAddress={selectedAddress}
                />
              );
            case "addName":
              return (
                <AddName
                  key="addName"
                  listName={listName}
                  importNameValidator={importNameValidator}
                  onListNameChange={onListNameChange}
                  stepNumber={stepNumber}
                />
              );
            case "addSource":
              return (
                <AddSource
                  key="addSource"
                  listSource={listSource}
                  onListSourceChange={onListSourceChange}
                  stepNumber={stepNumber}
                />
              );
          }
        })}
      </div>

      {!!numRecords && (
        <div className="gap-6 pt-4">
          <div className="flex flex-col md:flex-row  md:justify-between">
            <p className="font-semibold text-gray-primary text-2xl">
              File Preview
            </p>
          </div>
          {userCredits < numRecords && (
            <div>
              <p className="w-full py-4 bg-red-200 p-6 rounded-lg mt-4 text-red-800">
                You currently do not have sufficient credits for the number of
                records ({numRecords}) in the file. You can reduce the number of
                records in the file to match your number of credits (
                {userCredits}) remaining or call our Landvoice team at&nbsp;
                <strong>{CONTACT_MORE_CREDITS}</strong> to purchase more credits
              </p>
            </div>
          )}
          <div className="flex justify-center pb-4">
            <div className="inline-flex gap-12 px-8 py-4 bg-gray-50 rounded-lg">
              {[
                {
                  column: "File Name",
                  value: fileName,
                },
                {
                  column: "Total Rows",
                  value: numRecords,
                },
                {
                  column: "Total Columns",
                  value: fileColumnsKeys?.length,
                },
                {
                  column: "Credits Required",
                  value: numRecords,
                },
              ].map(({ column, value }, index) => (
                <div key={column} className={index === 0 ? "text-left" : "text-center"}>
                  <p className="text-xs font-bold text-gray-secondary uppercase tracking-wide">{column}</p>
                  <p className="text-base font-semibold text-gray-primary">{value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative flex flex-col w-full overflow-auto h-[400px] border rounded-lg">
            <table className="w-full text-left table-auto min-w-max">
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  {fileColumnsKeys.map((fileColumnKey) => (
                    <th
                      key={fileColumnKey}
                      className="p-4 border-b border-blue-gray-100 text-sm font-semibold text-gray-primary"
                    >
                      {fileColumnKey}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jsonData?.slice(0, NUM_PREVIEW_ROWS)?.map((row, rowIndex) => (
                  <tr key={`imported-row-${rowIndex}`}>
                    {fileColumnsKeys.map((fileColumnKey, cellIndex) => (
                      <td
                        className="p-4 border-b border-blue-gray-50 overflow-x-hidden whitespace-nowrap text-ellipsis max-w-80 text-sm font-medium text-nowrap text-gray-secondary"
                        key={`imported-row-${rowIndex}-${cellIndex}`}
                      >
                        {row[fileColumnKey] || "N/A"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default ConfigureUpload;
